import cloneDeep from 'lodash/cloneDeep';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { EMBEDDED_SHAPE_SET_LIB_ID_PREFIX } from '../../editor/library/abstract-shape-library';
import { IShapeSetPayload, IShapeSetUpsertResult, ShapeSetError } from './shape-set.types';

/**
 * In-memory store for embedded shape sets imported during the current session.
 * This registry is runtime only and does not persist data to diagram model.
 *
 * @author  sajeevah
 * @since   2026-02-20
 */
@Injectable()
export class ShapeSetRegistryService {

    private sets$ = new BehaviorSubject<Map<string, IShapeSetPayload>>( new Map());

    /**
     * Returns a deep-cloned snapshot of a set by id.
     */
    public getSet( setId: string ): IShapeSetPayload {
        const set = this.sets$.value.get( setId );
        return set ? cloneDeep( set ) : null;
    }

    /**
     * Emits updates for the given set id and returns deep-cloned values.
     */
    public getSet$( setId: string ): Observable<IShapeSetPayload> {
        return this.sets$.pipe(
            map( sets => sets.get( setId ) || null ),
            distinctUntilChanged(( a, b ) => {
                if ( !a && !b ) {
                    return true;
                }
                if ( !a || !b ) {
                    return false;
                }
                return a.id === b.id && a.version === b.version && JSON.stringify( a.meta ) === JSON.stringify( b.meta );
            }),
            map( set => set ? cloneDeep( set ) : null ),
        );
    }

    /**
     * Returns deep-cloned snapshots of all imported shape sets.
     */
    public getAllSets(): IShapeSetPayload[] {
        return Array.from( this.sets$.value.values()).map( set => cloneDeep( set ));
    }

    /**
     * Emits deep-cloned snapshots of all imported shape sets.
     */
    public getAllSets$(): Observable<IShapeSetPayload[]> {
        return this.sets$.pipe(
            map( sets => Array.from( sets.values()).map( set => cloneDeep( set ))),
        );
    }

    /**
     * Creates or updates a set with version-based conflict handling.
     */
    public upsertSet( incoming: IShapeSetPayload ): IShapeSetUpsertResult {
        const set = cloneDeep( incoming );
        const current = this.sets$.value.get( set.id );
        if ( !current ) {
            const next = new Map( this.sets$.value );
            next.set( set.id, set );
            this.sets$.next( next );
            return { action: 'created', set };
        }
        if ( set.version < current.version ) {
            throw new ShapeSetError(
                'VERSION_CONFLICT',
                { setId: set.id, existingVersion: current.version, incomingVersion: set.version },
                'Incoming set version is older than existing set version.',
            );
        }
        if ( set.version === current.version ) {
            return { action: 'ignored', set: current };
        }
        const next = new Map( this.sets$.value );
        next.set( set.id, set );
        this.sets$.next( next );
        return { action: 'updated', set };
    }

    /**
     * Builds the runtime library id used in left panel state.
     */
    public getLibraryId( setId: string ): string {
        return EMBEDDED_SHAPE_SET_LIB_ID_PREFIX + setId;
    }

    /**
     * Extracts set id from a runtime embedded shape-set library id.
     */
    public parseLibraryId( libraryId: string ): string {
        if ( !libraryId || !libraryId.startsWith( EMBEDDED_SHAPE_SET_LIB_ID_PREFIX )) {
            return null;
        }
        return libraryId.slice( EMBEDDED_SHAPE_SET_LIB_ID_PREFIX.length );
    }
}
